/**
 * Hand-rolled RFC 4180 reader for a Splitwise group export. No dependency:
 * `packages/core` ships into three bundles and the format is fully known,
 * so ~120 lines here beat a parser library in all of them.
 */
import { roundToCurrencyPrecision } from '@splitfiesta/shared';

import { normalizeLabel, tableResolution } from './labelResolution';
import type { LabelResolution, LabelResolutions } from './labelResolution';

export const SPLITWISE_FIXED_COLUMNS = [
  'Date',
  'Description',
  'Category',
  'Cost',
  'Currency',
] as const;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const BALANCE_TOLERANCE = 0.01;

export interface SplitwiseRow {
  /** 1-based among the rows returned in `rows`; blank and total lines consume none. */
  rowIndex: number;
  date: string;
  description: string;
  /** Raw Splitwise label, before any mapping. */
  category: string;
  cost: number;
  currency: string;
  /** One net effect (paid − share) per member column, in header order. */
  effects: number[];
  kind: 'expense' | 'payment' | 'skipped';
  skipReason?: 'no-balance-effect' | 'payment-shape';
}

export interface SplitwiseTotals {
  currency: string;
  perMember: number[];
}

export type SplitwiseWarning =
  | { code: 'unbalanced-row'; rowIndex: number; delta: number }
  | { code: 'no-balance-effect' | 'payment-shape'; rowIndex: number }
  | { code: 'unknown-category'; category: string; count: number };

export interface SplitwiseFile {
  /** The five fixed header cells, trimmed, in whatever language the export used. */
  headerLabels: string[];
  members: string[];
  rows: SplitwiseRow[];
  totals: SplitwiseTotals[];
  warnings: SplitwiseWarning[];
  /** Most frequent first. */
  currencies: string[];
  dateRange: { from: string; to: string };
  /** The map actually applied: the input merged with table hits and payment guesses. */
  resolutions: LabelResolutions;
}

export type SplitwiseParseErrorCode =
  | 'not-splitwise'
  | 'invalid-header'
  | 'invalid-row'
  | 'invalid-date'
  | 'invalid-cost'
  | 'invalid-currency'
  | 'empty';

export class SplitwiseParseError extends Error {
  constructor(
    public readonly code: SplitwiseParseErrorCode,
    public readonly rowIndex?: number,
    /** Only on `not-splitwise`, so the message can quote what the file starts with. */
    public readonly header?: string[]
  ) {
    super(
      `Splitwise CSV ${code}${rowIndex === undefined ? '' : ` at row ${rowIndex}`}`
    );
    this.name = 'SplitwiseParseError';
  }
}

export function parseCsvRows(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  while (i < src.length) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += ch === '\r' && src[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    i += 1;
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const cellAt = (cells: string[], index: number): string =>
  (cells[index] ?? '').trim();

const toNumber = (cell: string, rowIndex: number): number => {
  if (cell === '') return 0;
  if (!NUMBER_RE.test(cell)) {
    throw new SplitwiseParseError('invalid-cost', rowIndex);
  }
  return Number(cell);
};

/** Totals rows are reconciliation-only, so a junk cell must not abort the file. */
const toNumberOrZero = (cell: string): number =>
  NUMBER_RE.test(cell) ? Number(cell) : 0;

interface ParsedRow {
  rowIndex: number;
  date: string;
  description: string;
  category: string;
  label: string;
  cost: number;
  currency: string;
  effects: number[];
  nets: number[];
}

export function parseSplitwiseCsv(
  text: string,
  resolutions: LabelResolutions = {}
): SplitwiseFile {
  const table = parseCsvRows(text).filter(cells =>
    cells.some(cell => cell.trim() !== '')
  );
  if (table.length === 0) throw new SplitwiseParseError('empty');

  // Splitwise renders the header in the account's language, so only its
  // shape is checked; the words are kept for the error message.
  const header = table[0].map(cell => cell.trim());
  const firstMemberColumn = SPLITWISE_FIXED_COLUMNS.length;
  const headerLabels = header.slice(0, firstMemberColumn);
  const members = header.slice(firstMemberColumn);
  const namesUsable =
    members.every(name => name !== '') &&
    new Set(members).size === members.length;
  if (members.length < 2 || !namesUsable) {
    throw new SplitwiseParseError('invalid-header');
  }
  const rowWidth = firstMemberColumn + members.length;

  const parsed: ParsedRow[] = [];
  const totals: SplitwiseTotals[] = [];

  for (const cells of table.slice(1)) {
    const description = cellAt(cells, 1);
    const category = cellAt(cells, 2);
    const costCell = cellAt(cells, 3);
    const currency = cellAt(cells, 4).toUpperCase();

    // Checked before the date/cost validation below: total rows carry a single
    // space in Category and Cost, and a file-export date rather than a row date.
    if (category === '' && costCell === '' && description !== '') {
      totals.push({
        currency,
        perMember: members.map((_, i) =>
          toNumberOrZero(cellAt(cells, firstMemberColumn + i))
        ),
      });
      continue;
    }

    const rowIndex = parsed.length + 1;
    const date = cellAt(cells, 0);
    // With the header words unchecked, the first row's content is what tells a
    // bank or spreadsheet CSV from a Splitwise export.
    if (
      rowIndex === 1 &&
      (!DATE_RE.test(date) || !CURRENCY_RE.test(currency))
    ) {
      throw new SplitwiseParseError('not-splitwise', undefined, headerLabels);
    }
    // A short row cannot attribute its nets to members at all, which is the
    // same class of defect as a bad date or cost — abort rather than warn.
    if (cells.length < rowWidth) {
      throw new SplitwiseParseError('invalid-row', rowIndex);
    }
    if (!DATE_RE.test(date)) {
      throw new SplitwiseParseError('invalid-date', rowIndex);
    }

    const cost = toNumber(costCell, rowIndex);
    const effects = members.map((_, i) =>
      toNumber(cellAt(cells, firstMemberColumn + i), rowIndex)
    );
    parsed.push({
      rowIndex,
      date,
      description,
      category,
      label: normalizeLabel(category),
      cost,
      currency,
      effects,
      // Classified on the nets rounded to the row currency rather than on the
      // raw cells: on a 0-decimal currency (COP, JPY) a sub-unit net is money
      // no import can move, so the row is skipped here instead of reaching
      // reconstruction as an expense with nothing to split or a one-sided
      // payment.
      nets: effects.map(net => roundToCurrencyPrecision(net, currency)),
    });
  }

  const applied = resolveLabels(parsed, members, resolutions);
  const resolutionOf = (label: string): LabelResolution | undefined =>
    Object.hasOwn(applied, label) ? applied[label] : undefined;

  const rows: SplitwiseRow[] = [];
  const warnings: SplitwiseWarning[] = [];
  const currencyCounts = new Map<string, number>();
  const unknownCounts = new Map<string, number>();

  for (const row of parsed) {
    const { rowIndex, category, currency, effects, nets } = row;
    const resolution = resolutionOf(row.label);
    const positives = nets.filter(net => net > 0).length;
    const negatives = nets.filter(net => net < 0).length;
    let kind: SplitwiseRow['kind'] = 'expense';
    let skipReason: SplitwiseRow['skipReason'];

    if (resolution?.kind === 'payment') {
      if (positives === 1 && negatives === 1) {
        kind = 'payment';
      } else {
        kind = 'skipped';
        skipReason = 'payment-shape';
        warnings.push({ code: 'payment-shape', rowIndex });
      }
    } else if (positives === 0 && negatives === 0) {
      kind = 'skipped';
      skipReason = 'no-balance-effect';
      warnings.push({ code: 'no-balance-effect', rowIndex });
    }

    const delta = effects.reduce((sum, net) => sum + net, 0);
    if (Math.abs(delta) > BALANCE_TOLERANCE) {
      warnings.push({
        code: 'unbalanced-row',
        rowIndex,
        delta: Number(delta.toFixed(2)),
      });
    }

    // Only rows that will actually be imported shape the review screen, so a
    // skipped row contributes neither a currency nor an unknown-category count.
    if (kind !== 'skipped') {
      if (!CURRENCY_RE.test(currency)) {
        throw new SplitwiseParseError('invalid-currency', rowIndex);
      }
      currencyCounts.set(currency, (currencyCounts.get(currency) ?? 0) + 1);
      if (!resolution) {
        unknownCounts.set(category, (unknownCounts.get(category) ?? 0) + 1);
      }
    }

    rows.push({
      rowIndex,
      date: row.date,
      description: row.description,
      category,
      cost: row.cost,
      currency,
      effects,
      kind,
      ...(skipReason ? { skipReason } : {}),
    });
  }

  for (const [category, count] of unknownCounts) {
    warnings.push({ code: 'unknown-category', category, count });
  }

  const dates = rows.map(row => row.date).sort();
  return {
    headerLabels,
    members,
    rows,
    totals,
    warnings,
    // Array#sort is stable, so equal counts keep first-appearance order.
    currencies: [...currencyCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([code]) => code),
    dateRange: { from: dates[0] ?? '', to: dates[dates.length - 1] ?? '' },
    resolutions: applied,
  };
}

/**
 * Input map first, then the English table, then the payment guess. The guess
 * is per label, never per row: Splitwise never files a payment and an expense
 * under one label, so a single row without the payment shape makes the label
 * a category.
 */
function resolveLabels(
  parsed: ParsedRow[],
  members: string[],
  resolutions: LabelResolutions
): LabelResolutions {
  // Null prototype: labels come from the CSV, and a `__proto__` label must
  // land as an own key rather than replace the map's prototype.
  const applied: LabelResolutions = Object.assign(
    Object.create(null),
    resolutions
  );
  const rowsByLabel = new Map<string, ParsedRow[]>();
  for (const row of parsed) {
    const group = rowsByLabel.get(row.label);
    if (group) group.push(row);
    else rowsByLabel.set(row.label, [row]);
  }

  // Deduplicated so two members sharing a first name count as one name.
  const firstNames = [
    ...new Set(
      members.map(name => name.normalize('NFC').split(/\s+/)[0].toLowerCase())
    ),
  ].map(wordPattern);
  for (const [label, group] of rowsByLabel) {
    if (Object.hasOwn(applied, label)) continue;
    const fromTable = tableResolution(label);
    if (fromTable) applied[label] = fromTable;
    else if (group.every(row => hasPaymentShape(row, firstNames))) {
      applied[label] = { kind: 'payment', source: 'guess' };
    }
  }
  return applied;
}

const REGEX_META = /[.*+?^${}()|[\]\\]/g;

/**
 * Whole-word and case-insensitive, with Unicode boundaries: `\b` treats an
 * accented letter as a boundary, and a substring match finds "Al" in "total".
 */
function wordPattern(word: string): RegExp {
  return new RegExp(
    `(?<![\\p{L}\\p{M}\\p{N}_])${word.replace(REGEX_META, '\\$&')}(?![\\p{L}\\p{M}\\p{N}_])`,
    'iu'
  );
}

/** "<payer> paid <payee>" in any language: two members named, and one side receiving the whole cost. */
function hasPaymentShape(row: ParsedRow, firstNames: RegExp[]): boolean {
  const description = row.description.normalize('NFC');
  const named = firstNames.filter(name => name.test(description));
  if (named.length < 2) return false;
  const positives = row.nets.filter(net => net > 0);
  if (positives.length !== 1) return false;
  const cost = roundToCurrencyPrecision(row.cost, row.currency);
  return Math.abs(positives[0] - cost) < BALANCE_TOLERANCE;
}
